import { useEffect, useRef, useState } from 'react';
import { STRING_CONFIG, SETTING_CONFIG, DESIGN_CONFIG } from 'utils';
import { jwtDecode } from "jwt-decode";

export const getPrimeAttributeBackground = (attribute) => {
  if (attribute >= 0 && attribute < 2)
    return DESIGN_CONFIG.color.error.error50;
  if (attribute >= 2 && attribute < 3)
    return DESIGN_CONFIG.color.warning.warning50;
  if (attribute >= 3 && attribute < 4)
    return DESIGN_CONFIG.color.success.success50;
  if (attribute >= 4 && attribute < 5)
    return DESIGN_CONFIG.color.primary.primary50;
  if (attribute === 5) return DESIGN_CONFIG.color.gradient.highlight;
  return DESIGN_CONFIG.color.primary.primary50;
};

export const getMetricColor = (metric) => {
  if (metric >= 0 && metric < 2) return DESIGN_CONFIG.color.error.errorMain;
  if (metric >= 2 && metric < 3)
    return DESIGN_CONFIG.color.warning.warningMain;
  if (metric >= 3 && metric < 4)
    return DESIGN_CONFIG.color.success.successMain;
  if (metric >= 4 && metric < 5)
    return DESIGN_CONFIG.color.primary.primaryMain;
  if (metric === 5) return DESIGN_CONFIG.color.neutral.neutral500;
  return DESIGN_CONFIG.color.primary.primaryMain;
};

export const getMetricBackground = (metric) => {
  if (metric >= 0 && metric < 2) return DESIGN_CONFIG.color.error.error50;
  if (metric >= 2 && metric < 3) return DESIGN_CONFIG.color.warning.warning50;
  if (metric >= 3 && metric < 4) return DESIGN_CONFIG.color.success.success50;
  if (metric >= 4 && metric < 5) return DESIGN_CONFIG.color.primary.primary50;
  if (metric === 5) return DESIGN_CONFIG.color.gradient.highlight;
  return DESIGN_CONFIG.color.primary.primary50;
};

export const useScrollCollapse = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isCollapsedRef = useRef(false);
  const lastScrollY = useRef(window.scrollY);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (ticking.current) return;

      ticking.current = true;
      requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;

        if (
          currentScrollY > lastScrollY.current &&
          currentScrollY > 80 &&
          !isCollapsedRef.current
        ) {
          isCollapsedRef.current = true;
          setIsCollapsed(true);
        }

        if (
          currentScrollY < lastScrollY.current &&
          currentScrollY < 40 &&
          isCollapsedRef.current
        ) {
          isCollapsedRef.current = false;
          setIsCollapsed(false);
        }

        lastScrollY.current = currentScrollY;
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return isCollapsed;
};

export const parseStoreTime = (timeString, period, format24Hour = false) => {
  if (!timeString || typeof timeString !== 'string') {
    return null;
  }

  timeString = timeString.trim();

  const timeRegex = /^([0-1][0-9]|2[0-3]):([0-5][0-9])(?::([0-5][0-9]))?$/;
  if (!timeRegex.test(timeString)) {
    return null;
  }

  if (timeString === '00:00' || timeString === '00:00:00') {
    return null;
  }

  try {
    const parts = timeString.split(':');
    const hour = parseInt(parts[0], 10);
    const min = parseInt(parts[1], 10);

    if (format24Hour) {
      return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
    }

    let displayHour = hour;
    let displayPeriod = period === 'close' ? 'PM' : 'AM';

    if (hour === 0) {
      displayHour = 12;
    } else if (hour > 12) {
      displayHour = hour - 12;
    }

    const result = `${displayHour}:${min.toString().padStart(2, '0')}${displayPeriod}`;
    return result;
  } catch (error) {
    return null;
  }
};

export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    return decoded.exp < currentTime;
  } catch (error) {
    return true;
  }
};

export async function buildTrialHeader(secret) {
  const enc = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const payload = { type: "trial", ts: Date.now() };

  const keyBytes = await crypto.subtle.digest("SHA-256", enc.encode(secret));
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const encrypted = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      enc.encode(JSON.stringify(payload))
    )
  );

  const tagLength = 16;
  const ciphertext = encrypted.slice(0, encrypted.length - tagLength);
  const tag = encrypted.slice(encrypted.length - tagLength);

  const toB64 = (bytes) => btoa(String.fromCharCode(...bytes));

  return `${toB64(iv)}.${toB64(ciphertext)}.${toB64(tag)}`;
}

export const formatPrice = (price, fallback = '-') => {
  if (price === null || price === undefined || price === '' || isNaN(price)) {
    return fallback;
  }
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  const formattedPrice = new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(numericPrice);

  return `₹ ${formattedPrice}`;
};

export const isPrimeAttributeRangeValid = (primeAttributeRange) => {
  if (!Array.isArray(primeAttributeRange) || primeAttributeRange.length < 2) {
    return false;
  }

  const primeAttr1 = primeAttributeRange[0]?.prime_attribute_1;
  if (
    !primeAttr1 ||
    typeof primeAttr1 !== 'object' ||
    !primeAttr1?.all ||
    typeof primeAttr1.all?.min !== 'number' ||
    typeof primeAttr1.all?.max !== 'number' ||
    !primeAttr1?.common ||
    typeof primeAttr1.common?.min !== 'number' ||
    typeof primeAttr1.common?.max !== 'number' ||
    typeof primeAttr1?.unit !== 'string'
  ) {
    return false;
  }

  const primeAttr2 = primeAttributeRange[1]?.prime_attribute_2;
  if (
    !primeAttr2 ||
    typeof primeAttr2 !== 'object' ||
    !primeAttr2?.all ||
    typeof primeAttr2?.all?.min !== 'number' ||
    typeof primeAttr2?.all?.max !== 'number' ||
    !primeAttr2?.common ||
    typeof primeAttr2?.common.min !== 'number' ||
    typeof primeAttr2?.common.max !== 'number' ||
    typeof primeAttr2?.unit !== 'string'
  ) {
    return false;
  }

  return true;
};

export function getYearsFromMonths(months) {
  if (months < 0) {
    return 'Invalid: Months value';
  }
  const years = Math.floor(months / 12);

  const remainingMonths = months % 12;

  if (years === 0 && remainingMonths === 0) {
    return 'Invalid: Months value';
  }
  return `${years > 0 ? `${years} ${years === 1 ? 'year' : 'years'}` : ''}${remainingMonths ? `${years > 0 ? ' and ' : ''}${remainingMonths} months` : ''}`;
}

export const computeAttributes = (attributes, attributeNames, cCode) => {
  const sections = [
    Object.keys(STRING_CONFIG.entityCaption.category[cCode].product)[0],
    Object.keys(STRING_CONFIG.entityCaption.category[cCode].product)[2],
    Object.keys(STRING_CONFIG.entityCaption.category[cCode].product)[1],
    Object.keys(STRING_CONFIG.entityCaption.category[cCode].product)[3],
  ];

  const ranges = [
    [1, 6],
    [7, 12],
    [13, 18],
    [19, 24],
  ];

  const attributeRanges = sections.reduce((acc, section, index) => {
    acc[section] = ranges[index];
    return acc;
  }, {});

  const similarAttributes = {};

  const differentAttributes = {};

  sections.forEach((section) => {
    similarAttributes[section] = {};

    differentAttributes[section] = {};

    const [start, end] = attributeRanges[section];

    for (let attributeCnt = start; attributeCnt <= end; attributeCnt += 1) {
      const attributeKey = `attribute_${attributeCnt}`;

      const descriptiveKey = attributeNames[section][`attr${attributeCnt}`];

      if (!descriptiveKey) {
        /* eslint-disable-next-line no-continue */
        continue;
      }

      const ProductOneAttributeVal =
        attributes.compareProductOneAttributes?.[section][attributeKey];

      const ProductTwoAttributeVal =
        attributes.compareProductTwoAttributes?.[section][attributeKey];

      const ProductThreeAttributeVal =
        attributes.compareProductThreeAttributes?.[section][attributeKey];

      const attributeValues = [
        ProductOneAttributeVal,
        ProductTwoAttributeVal,
        ProductThreeAttributeVal,
      ].filter((val) => val !== undefined);

      if (attributeValues.length === 0) {
        /* eslint-disable-next-line no-continue */
        continue;
      }

      const isSimilar = attributeValues.every(
        (val) => val === attributeValues[0]
      );

      if (isSimilar) {
        similarAttributes[section][descriptiveKey] = {};
        if (ProductOneAttributeVal !== undefined)
          similarAttributes[section][descriptiveKey].productOne =
            ProductOneAttributeVal;
        if (ProductTwoAttributeVal !== undefined)
          similarAttributes[section][descriptiveKey].productTwo =
            ProductTwoAttributeVal;
        if (ProductThreeAttributeVal !== undefined)
          similarAttributes[section][descriptiveKey].productThree =
            ProductThreeAttributeVal;
      } else {
        differentAttributes[section][descriptiveKey] = {};
        if (ProductOneAttributeVal !== undefined)
          differentAttributes[section][descriptiveKey].productOne =
            ProductOneAttributeVal;
        if (ProductTwoAttributeVal !== undefined)
          differentAttributes[section][descriptiveKey].productTwo =
            ProductTwoAttributeVal;
        if (ProductThreeAttributeVal !== undefined)
          differentAttributes[section][descriptiveKey].productThree =
            ProductThreeAttributeVal;
      }
    }
  });
  return { similarAttributes, differentAttributes };
};

export function scaleAttributeFromJSON(attributeObject, cCode) {
  try {
    console.log('🔍 scaleAttributeFromJSON INPUT:', { attributeObject, cCode });

    const attributeValue = attributeObject;
    const { value } = attributeValue;
    const phyQtyId = attributeValue['phy_qty_id'];

    console.log('📊 Extracted values:', { value, phyQtyId });

    if (!phyQtyId || value == null) {
      console.log('⚠️ Missing phyQtyId or value, returning 0');
      return 0;
    }
    const { scaleFactor } = SETTING_CONFIG.unitConversion[cCode][phyQtyId];

    let result = 0;

    if (typeof value === 'number') {
      result = value * scaleFactor;
      return result;
    }

    if (typeof value === 'object') {
      const firstKey = Object.keys(value)[0];

      if (firstKey && typeof value[firstKey] === 'number') {
        result = value[firstKey] * scaleFactor;
        console.log('✅ Object value multiplication:', { value: value[firstKey], scaleFactor, result });
        return result;
      }
    }
    return 0;
  } catch (error) {
    console.error('💥 scaleAttributeFromJSON ERROR:', error);
    return null;
  }
}

function scaleAttribute(attributeValue, scaleFactor = 1, unitSymbol = '') {
  const formatValue = (val) => {
    const scaled = val * scaleFactor;
    return scaled % 1 === 0
      ? `${scaled}${unitSymbol}`
      : `${scaled.toFixed(2)}${unitSymbol}`;
  };
  if (typeof attributeValue === 'number') {
    return formatValue(attributeValue);
  }
  if (typeof attributeValue === 'object' && attributeValue !== null) {
    return Object.values(attributeValue)
      .map((val) => (typeof val === 'number' ? formatValue(val) : val))
      .filter((val) => val !== '')
      .join(', ');
  }
  return attributeValue;
}

export function SimplifyAttributes(attributes, cCode) {
  if (!attributes) return {};
  const parsedAttributes =
    typeof attributes === 'string' ? JSON.parse(attributes) : attributes;

  function processAttributes(obj, cCode) {
    const result = Array.isArray(obj) ? [] : {};

    Object.entries(obj).forEach(([key, value]) => {
      if (key === 'attribute_3' && value && typeof value === 'object' && !('value' in value)) {
        try {
          const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
          if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
            result[key] = parsedValue;
          } else {
            result[key] = value;
          }
        } catch (e) {
          result[key] = value;
        }
      }
      else if (typeof value === 'string') {
        try {
          const parsedValue = JSON.parse(value);
          if (parsedValue && typeof parsedValue === 'object') {
            result[key] = parsedValue;
          } else {
            result[key] = value;
          }
        } catch (e) {
          result[key] = value;
        }
      } else if (value && typeof value === 'object') {
        if ('value' in value) {
          const { value: attributeValue, 'phy_qty_id': phyQtyId } = value;
          if (
            attributeValue === '' ||
            attributeValue === null ||
            attributeValue === undefined
          ) {
            result[key] = 'N/A';
            return;
          }
          let scaleFactor = 1;
          let unitSymbol = '';
          if (
            phyQtyId &&
            SETTING_CONFIG.unitConversion[cCode][phyQtyId]
          ) {
            scaleFactor =
              SETTING_CONFIG.unitConversion[cCode][phyQtyId].scaleFactor;

            unitSymbol =
              SETTING_CONFIG.unitConversion[cCode][phyQtyId].unitSymbol;
          }
          result[key] = scaleAttribute(attributeValue, scaleFactor, unitSymbol);
        } else {
          result[key] = processAttributes(value, cCode);
        }
      } else if (value === null || value === undefined || value === '') {
        result[key] = 'N/A';
      } else {
        result[key] = value;
      }
    });

    return result;
  }
  return processAttributes(parsedAttributes, cCode);
}

export function capitalizeWord(str) {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const camelize = (str) =>
  str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '');

export const notSpecialCharacter = (string) => /[^a-zA-Z0-9]+/g.test(string);

export function getTextForValue(value) {
  if (value >= 0 && value < 2) {
    return `${STRING_CONFIG.entityCaption.review.ratingSegment.one}`;
  }
  if (value >= 2 && value < 3) {
    return `${STRING_CONFIG.entityCaption.review.ratingSegment.two}`;
  }
  if (value >= 3 && value < 4) {
    return `${STRING_CONFIG.entityCaption.review.ratingSegment.three}`;
  }
  if (value >= 4 && value < 5) {
    return `${STRING_CONFIG.entityCaption.review.ratingSegment.four}`;
  }
  if (value === 5) {
    return `${STRING_CONFIG.entityCaption.review.ratingSegment.five}`;
  }
  return 'unknown';
}

export const truncateFullName = (fullName) => {
  if (fullName?.length <= 18) {
    return fullName;
  }
  return `${fullName?.slice(0, 24)}...`;
};

export function formatNumberAsRepresentation(value) {
  if (value === null || value === undefined || value === '' || Number(value) === 0) {
    return `No ${STRING_CONFIG.inPage.product.txtReviewTitle}`;
  }

  const number = Number(value);

  if (isNaN(number)) {
    return `No ${STRING_CONFIG.inPage.product.txtReviewTitle}`;
  }

  if (number < 100) {
    return `${String(number)} ${STRING_CONFIG.inPage.product.txtReviewTitle}`;
  }

  const rounded = Math.floor(number / 100) * 100;
  return `${rounded}+ ${STRING_CONFIG.inPage.product.txtReviewTitle}`;
}

export function formatDate(dateString) {
  if (dateString === undefined) return '';
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  };

  const date = new Date(dateString);
  return date.toLocaleString('en-IN', options);
}

export function getAdsDurationString(amount) {
  const daysMapping = {
    5: '1 day',
    25: '7 days',
    65: '30 days',
    450: '1 year',
    999: 'forever',
  };
  return `${daysMapping[amount] || 'unknown duration'}`;
}

export default function useClickOutside(ref, fun, ignoreRef) {
  useEffect(() => {
    const listener = (e) => {
      const isAntDropdown = e.target.closest('.ant-select-dropdown, .ant-picker-dropdown');

      const isIgnoredElement = ignoreRef?.current?.contains(e.target);

      if (!ref.current || ref.current.contains(e.target) || isAntDropdown || isIgnoredElement) {
        return;
      }
      fun();
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, ignoreRef]);
}

export function useMedia(query) {
  const isClient = typeof window !== 'undefined';

  const [matches, setMatches] = useState(() =>
    isClient ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (!isClient) return;

    const mediaQuery = window.matchMedia(query);

    const updateMatches = (event) => {
      setMatches(event.matches);
    };

    updateMatches(mediaQuery);

    mediaQuery.addEventListener('change', updateMatches);
    return () => mediaQuery.removeEventListener('change', updateMatches);
  }, [query, isClient]);

  return matches;
}

export const useScrollToTop = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const onPopState = () => {
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  return null;
};

export const getProductValue = (productValue, totalReviews) => {
  const TR = parseFloat(totalReviews);
  if (
    productValue == null ||
    productValue == 0 ||
    totalReviews == null ||
    isNaN(TR) ||
    TR < 10
  ) {
    return '--';
  }
  return productValue;
};

export function mapPriceToDays(input) {
  const number = typeof input === 'string' ? +input : input;

  if (number <= 10) return '1';
  if (number <= 30) return '7';
  if (number <= 100) return '30';
  if (number <= 500) return '365';
  return '1825';
}
